import { useFormik } from "formik";
import React, { forwardRef, useImperativeHandle } from "react";
import { useFragment } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import {
	ButtonKind,
	pageButtonForm_ButtonFragment$key,
} from "@relay/pageButtonForm_ButtonFragment.graphql";
import { PAGE_BUTTON_FRAGMENT } from "./page-button-form.graphql";
import {
	PageButtonFormProps,
	PageButtonFormRef,
	PageButtonFormSchema,
	PageButtonFormState,
} from "./page-button-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { PageButtonKindDropdown } from "../../../../components/page-button-kind-dropdown";
import { ValidatedField } from "../../../../components/ValidatedField";

export const PageButtonForm = forwardRef<PageButtonFormRef, PageButtonFormProps>(
	function PageButtonForm({ buttonFragmentRef, onSubmit }, ref) {
		const { t } = useTranslation("common");
		const data = useFragment<pageButtonForm_ButtonFragment$key>(
			PAGE_BUTTON_FRAGMENT,
			buttonFragmentRef ?? null,
		);

		const initialData = {
			text: data?.button?.text ?? "",
			kind: data?.button?.kind ?? "",
			url: data?.button?.url ?? "",
		};

		const formik = useFormik<PageButtonFormState>({
			initialValues: initialData,
			validationSchema: PageButtonFormSchema,
			onSubmit: (values) => {
				onSubmit?.(values);
			},
		});
		useImperativeHandle(ref, () => ({
			submit: formik.submitForm,
			validate: () =>
				formik.validateForm().then((errors) => Object.keys(errors).length === 0),
			values: formik.values,
		}));

		return (
			<>
				<ValidatedField<PageButtonFormState, ButtonKind>
					name={"kind"}
					label={t("messageDefinition.pageButton")}
					required
					component={PageButtonKindDropdown}
					formikConfig={formik}
				/>
				<ValidatedField<PageButtonFormState, string>
					name="text"
					label={t("forms.text")}
					required
					component={DefaultTextFieldComponent}
					formikConfig={formik}
				/>
				{formik.values.kind === "link" && (
					<ValidatedField<PageButtonFormState, string>
						name="url"
						required
						label={t("forms.url")}
						component={DefaultTextFieldComponent}
						formikConfig={formik}
					/>
				)}
			</>
		);
	},
);
